/**
 * Settings Screen Modal
 * Location selector, dark mode toggle, and links (Privacy Policy, Terms of Use).
 * Each row is a rounded surface card matching the Figma V2 design.
 */

import { useState } from 'react';
import { CloseButton } from '../ui/CloseButton';
import { NRLogo } from '../ui/NRLogo';
import { ChevronDown, ChevronRight } from '../ui/icons';
import { ToggleSwitch } from '../ui/ToggleSwitch';
import { useI18n } from '../../i18n';

const SETTINGS_LINK_KEYS = [
  { key: 'settings.privacy' as const, href: 'https://nowramp.com/privacy-policy' },
  { key: 'settings.terms' as const, href: 'https://nowramp.com/terms-and-conditions' },
];

const cardStyle = {
  background: 'var(--t-surface)',
  borderRadius: 'var(--t-card-radius)',
} as const;

export interface SettingsScreenProps {
  onClose: () => void;
  darkMode: boolean;
  onToggleDarkMode: (v: boolean) => void;
  skinId: string;
  onSelectSkin: (id: string) => void;
  /** Show dark mode toggle (default: true) */
  showThemeToggle?: boolean;
  /** Currently detected/selected country code */
  country?: string;
  /** Display name for the country */
  countryName?: string;
  /** Callback when location row is pressed */
  onLocationPress?: () => void;
  /** Callback when language row is pressed (opens language picker modal) */
  onLanguagePress?: () => void;
  /** Base URL for static assets (flags) */
  assetBaseUrl?: string;
}

export function SettingsScreen({
  onClose,
  darkMode,
  onToggleDarkMode,
  skinId: _skinId,
  onSelectSkin: _onSelectSkin,
  showThemeToggle = true,
  country,
  countryName,
  onLocationPress,
  onLanguagePress,
  assetBaseUrl = '',
}: SettingsScreenProps) {
  void _skinId;
  void _onSelectSkin;

  const { t, locale, languages } = useI18n();
  const [flagError, setFlagError] = useState(false);

  return (
    <>
      {/* Modal header — 80px with blurred bg */}
      <div className="relative shrink-0" style={{ height: 80, background: 'var(--t-header-bg)', backdropFilter: 'blur(var(--t-header-blur))', WebkitBackdropFilter: 'blur(var(--t-header-blur))' }}>
        <div className="absolute" style={{ left: 12, top: 20 }}>
          <NRLogo width={46} />
        </div>
        <div className="absolute" style={{ right: 12, top: 20 }}>
          <CloseButton onClick={onClose} />
        </div>
      </div>
      <div className="px-3 flex-1 pt-4 overflow-y-auto custom-scroll" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {/* Location card — label inside, flag + country + chevron */}
        {onLocationPress && (
          <button
            onClick={onLocationPress}
            className="w-full text-left transition-colors card-press modal-item-in"
            style={{ ...cardStyle, backgroundColor: 'var(--t-surface)', color: 'inherit', border: 'none', cursor: 'pointer', padding: '12px 16px' }}
          >
            <span className="text-[13px] block mb-1.5" style={{ color: 'var(--t-text-secondary)' }}>
              {t('settings.location')}
            </span>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {country && !flagError ? (
                  <img
                    src={`${assetBaseUrl}/flags/${country.toLowerCase()}.svg`}
                    alt={country}
                    width={28}
                    height={28}
                    style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }}
                    onError={() => setFlagError(true)}
                  />
                ) : (
                  <GlobeIcon size={28} />
                )}
                <span className="text-[15px] font-medium" style={{ color: 'var(--t-text)' }}>
                  {countryName || country}
                </span>
              </div>
              <ChevronDown />
            </div>
          </button>
        )}

        {/* Dark mode card */}
        {showThemeToggle && (
          <div
            className="flex items-center justify-between w-full px-4 modal-item-in"
            style={{ ...cardStyle, paddingTop: 14, paddingBottom: 14, animationDelay: '60ms' }}
          >
            <span className="font-medium text-[15px]" style={{ color: 'var(--t-text)' }}>
              {t('settings.darkMode')}
            </span>
            <ToggleSwitch checked={darkMode} onChange={onToggleDarkMode} />
          </div>
        )}

        {/* Language card */}
        {onLanguagePress && (
          <button
            onClick={onLanguagePress}
            className="flex items-center justify-between w-full transition-colors card-press modal-item-in"
            style={{ ...cardStyle, backgroundColor: 'var(--t-surface)', color: 'inherit', border: 'none', cursor: 'pointer', padding: '14px 16px', margin: 0, textAlign: 'left', animationDelay: '120ms' }}
          >
            <span className="font-medium text-[15px]" style={{ color: 'var(--t-text)' }}>
              {t('settings.language')}
            </span>
            <span className="text-[13px]" style={{ color: 'var(--t-text-secondary)' }}>
              {languages.find(l => l.code === locale)?.name || locale}
            </span>
          </button>
        )}

        {/* Links: Privacy Policy, Terms of Use — each in its own card */}
        {SETTINGS_LINK_KEYS.map((item, i) => (
          <a
            key={item.key}
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between w-full px-4 transition-colors card-press modal-item-in"
            style={{ ...cardStyle, textDecoration: 'none', color: 'inherit', paddingTop: 14, paddingBottom: 14, animationDelay: `${180 + i * 60}ms` }}
          >
            <span className="font-medium text-[15px]" style={{ color: 'var(--t-text)' }}>
              {t(item.key)}
            </span>
            <ChevronRight />
          </a>
        ))}
      </div>
    </>
  );
}

function GlobeIcon({ size = 28 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
      <circle cx="20" cy="20" r="18" stroke="var(--t-text-secondary)" strokeWidth="1.5" fill="none" />
      <ellipse cx="20" cy="20" rx="9" ry="18" stroke="var(--t-text-secondary)" strokeWidth="1.5" fill="none" />
      <line x1="2" y1="20" x2="38" y2="20" stroke="var(--t-text-secondary)" strokeWidth="1.5" />
      <path d="M5 12h30M5 28h30" stroke="var(--t-text-secondary)" strokeWidth="1.2" />
    </svg>
  );
}
